import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Trash2, ShoppingCart, ArrowLeft, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useWishlist } from '@/lib/wishlist-context';
import { useCart } from '@/lib/cart-context';
import { useCurrency } from '@/lib/currency-context';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { buildProductPath } from '@/lib/product-url';
import { productNeedsVariant } from '@/lib/variant';

const WishlistPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, loading, removeFromWishlist, clearWishlist } = useWishlist();
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();
  
  // Get clean language code (remove POSIX locale suffixes)
  const currentLang = (i18n.language || 'tr').split('@')[0].substring(0, 2);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user && !loading) {
      navigate(`/${currentLang}/login`);
    }
  }, [user, loading, navigate, currentLang]);

  const handleAddToCart = async (product) => {
    if (productNeedsVariant(product)) {
      navigate(buildProductPath(currentLang, product));
      return;
    }
    addItem(product, 1);
    toast.success(t('cart.addedToCart'), {
      description: product.name,
    });
    
    // Remove from wishlist automatically (silent = true to not show extra toast)
    await removeFromWishlist(product.id, null, true);
  };

  const handleRemove = async (productId, productName) => {
    await removeFromWishlist(productId, productName);
  };

  const handleClearAll = async () => {
    if (window.confirm(t('wishlist.confirmClear') || 'Tüm favorileri silmek istediğinizden emin misiniz?')) {
      await clearWishlist();
    }
  };

  const getProductName = (product) => {
    const lang = currentLang.substring(0, 2);
    return product.translations?.[lang]?.name || product.name;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-10 w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="wishlist-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-500 fill-red-500" />
              {t('wishlist.title') || 'Favorilerim'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {items.length > 0 
                ? `${items.length} ${t('wishlist.items') || 'ürün'}`
                : t('wishlist.empty') || 'Henüz favori ürününüz yok'
              }
            </p>
          </div>
        </div>
        
        {items.length > 0 && (
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t('wishlist.clearAll') || 'Tümünü Temizle'}
          </Button>
        )}
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <Card className="text-center py-16">
          <CardContent>
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('wishlist.emptyTitle') || 'Favorileriniz boş'}
            </h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {t('wishlist.emptyDescription') || 'Beğendiğiniz ürünleri favorilere ekleyerek daha sonra kolayca bulabilirsiniz.'}
            </p>
            <Button onClick={() => navigate(`/${currentLang}/products`)}>
              <Package className="w-4 h-4 mr-2" />
              {t('wishlist.browseProducts') || 'Ürünleri Keşfet'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Wishlist Items */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item) => {
            const product = item.product;
            if (!product) return null;
            
            const productName = getProductName(product);

            return (
              <Card 
                key={item.product_id} 
                className="group overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`wishlist-item-${item.product_id}`}
              >
                <Link to={buildProductPath(currentLang, product)}>
                  <div className="relative aspect-square bg-gray-100">
                    <ProductThumbnail
                      src={product.images?.[0]}
                      alt={productName}
                      className="h-full w-full"
                      coverImgClassName="group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Remove button */}
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute top-3 right-3 w-9 h-9 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-red-50 hover:text-red-600"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(product.id, productName);
                      }}
                      data-testid={`remove-wishlist-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Link>
                
                <CardContent className="p-4">
                  <Link to={buildProductPath(currentLang, product)}>
                    <h3 className="font-medium text-sm line-clamp-2 hover:text-primary transition-colors mb-2">
                      {productName}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold text-primary">
                      {formatPrice(product.price, product.currency || 'EUR')}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => handleAddToCart(product)}
                      className="rounded-full"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      {t('product.addToCart') || 'Sepete Ekle'}
                    </Button>
                  </div>
                  
                  {item.added_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('wishlist.addedOn') || 'Eklendi:'} {new Date(item.added_at).toLocaleDateString(currentLang === 'tr' ? 'tr-TR' : 'en-US')}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
